import { AxiosHeaders, type AxiosResponse } from "axios";

/**
 * Reduce an AxiosResponse to the structured-clone-safe subset the app actually
 * uses: `data`, `status`, `statusText`, `headers` — exactly kubb's
 * `ResponseConfig` (the type the generated `dataReturnType: "full"` hooks already
 * expose; the raw runtime object is wider than its declared type).
 *
 * The dropped `config` (axios transform/adapter *functions*) and `request` (an
 * `XMLHttpRequest`) cannot survive `structuredClone`. The cross-tab query
 * broadcast (lib/queryBroadcast.ts) posts cached query data over a
 * `BroadcastChannel`, which clones via `structuredClone`; a raw AxiosResponse
 * there throws `DataCloneError: ... could not be cloned` (Sentry). Persistence
 * survived only because `JSON.stringify` silently drops functions.
 *
 * `x-cursor`/`x-etags` response headers carry JSON payloads; parse them here so
 * every consumer (pagination, etags) reads structured values, not raw strings.
 * A malformed header is left as its raw string rather than thrown — a parse
 * error must not turn an otherwise-successful response into a rejected request.
 */
function parseJsonHeader(headers: Record<string, unknown>, name: string): void {
  const value = headers[name];
  if (typeof value !== "string") {
    return;
  }
  try {
    headers[name] = JSON.parse(value);
  } catch {
    // Leave the raw string; a bad header shouldn't fail the whole response.
  }
}

export function toBroadcastSafeResponse<T>(
  response: AxiosResponse<T>,
): AxiosResponse<T> {
  const raw = response.headers;
  const headers: Record<string, unknown> =
    raw instanceof AxiosHeaders ? raw.toJSON() : { ...raw };

  parseJsonHeader(headers, "x-cursor");
  parseJsonHeader(headers, "x-etags");

  return {
    data: response.data,
    status: response.status,
    statusText: response.statusText,
    headers,
  } as unknown as AxiosResponse<T>;
}
